import { useEffect, useMemo, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Download, Save } from "lucide-react";

const ACTIONS = ["view", "create", "edit", "delete", "approve"];

function valueText(value) {
  if (value === null || value === undefined || value === "") {
    return "—";
  }
  return typeof value === "object" ? JSON.stringify(value) : String(value);
}

export default function HRMSGovernance() {
  const [roles, setRoles] = useState([]);
  const [selectedRole, setSelectedRole] = useState("");
  const [logs, setLogs] = useState([]);
  const [filters, setFilters] = useState({
    actor_id: "",
    module: "all",
    start_date: "",
    end_date: "",
  });
  const [saving, setSaving] = useState(false);
  const [override, setOverride] = useState({
    module: "employee",
    recordId: "",
    reason: "",
    changes: "{}",
  });

  const selected = useMemo(
    () => roles.find((role) => role.key === selectedRole),
    [roles, selectedRole],
  );

  const query = () => ({
    actor_id: filters.actor_id || undefined,
    module: filters.module === "all" ? undefined : filters.module,
    start_date: filters.start_date || undefined,
    end_date: filters.end_date || undefined,
  });

  const loadRoles = async () => {
    try {
      const { data } = await api.get("/admin/hrms-roles-permissions");
      setRoles(data.roles || []);
      setSelectedRole((current) => current || data.roles?.[0]?.key || "");
    } catch (error) {
      toast.error(apiErr(error));
    }
  };

  const loadLogs = async () => {
    try {
      const { data } = await api.get("/admin/audit-logs", { params: query() });
      setLogs(data.items || []);
    } catch (error) {
      toast.error(apiErr(error));
    }
  };

  useEffect(() => {
    const initialize = async () => {
      try {
        const [roleResponse, auditResponse] = await Promise.all([
          api.get("/admin/hrms-roles-permissions"),
          api.get("/admin/audit-logs"),
        ]);
        setRoles(roleResponse.data.roles || []);
        setSelectedRole(roleResponse.data.roles?.[0]?.key || "");
        setLogs(auditResponse.data.items || []);
      } catch (error) {
        toast.error(apiErr(error));
      }
    };
    initialize();
  }, []);

  const updateCell = (module, action, checked) => {
    if (!selected || selected.key === "super_admin") {
      return;
    }
    setRoles((current) =>
      current.map((role) =>
        role.key === selected.key
          ? {
              ...role,
              matrix: {
                ...role.matrix,
                [module]: {
                  ...role.matrix[module],
                  [action]: checked,
                },
              },
            }
          : role,
      ),
    );
  };

  const saveMatrix = async () => {
    if (!selected) {
      return;
    }
    setSaving(true);
    try {
      await api.put(`/admin/hrms-roles-permissions/${selected.key}`, {
        matrix: selected.matrix,
      });
      toast.success("Roles and permissions updated");
      loadRoles();
    } catch (error) {
      toast.error(apiErr(error));
    } finally {
      setSaving(false);
    }
  };

  const exportAudit = async () => {
    try {
      const response = await api.get("/admin/audit-logs/export", {
        params: query(),
        responseType: "blob",
      });
      const url = URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = "nexreq-audit-log.csv";
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(apiErr(error));
    }
  };

  const runOverride = async (action) => {
    if (!override.recordId || override.reason.trim().length < 3) {
      toast.error("Record ID and a reason are required");
      return;
    }
    let payload = { reason: override.reason };
    if (action === "edit") {
      try {
        payload = { ...payload, changes: JSON.parse(override.changes || "{}") };
      } catch {
        toast.error("Changes must be valid JSON");
        return;
      }
    }
    setSaving(true);
    try {
      const endpoint =
        action === "edit"
          ? `/admin/records/${override.module}/${override.recordId}`
          : `/admin/records/${override.module}/${override.recordId}/${action}`;
      const method = action === "edit" ? "put" : "post";
      const { data } = await api[method](endpoint, payload);
      toast.success(
        data.recalculated_payslips
          ? `Saved and recalculated ${data.recalculated_payslips} payslip(s)`
          : "Super Admin change saved",
      );
      loadLogs();
    } catch (error) {
      toast.error(apiErr(error));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-4 space-y-6" data-testid="hrms-governance-workspace">
      <Card className="p-5" data-testid="roles-permissions-screen">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="font-display text-lg font-semibold">Roles & Permissions</div>
            <div className="mt-1 text-sm text-muted-foreground">
              Canonical HRMS access mapped to existing Nexreq roles without changing user accounts.
            </div>
          </div>
          <div className="w-full sm:w-64">
            <Label className="text-xs">Canonical role</Label>
            <Select value={selectedRole} onValueChange={setSelectedRole}>
              <SelectTrigger data-testid="hrms-permissions-role-select">
                <SelectValue placeholder="Select role" />
              </SelectTrigger>
              <SelectContent>
                {roles.map((role) => (
                  <SelectItem key={role.key} value={role.key}>
                    {role.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        {selected && (
          <>
            <div className="mt-3 text-xs text-muted-foreground">
              Existing roles: {selected.legacy_roles.join(", ") || "No legacy mapping"}
            </div>
            <div className="mt-5 overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Module</TableHead>
                    {ACTIONS.map((action) => (
                      <TableHead key={action} className="capitalize">
                        {action}
                      </TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {Object.entries(selected.matrix).map(([module, matrix]) => (
                    <TableRow key={module} data-testid={`permission-row-${module}`}>
                      <TableCell className="font-medium capitalize">
                        {module.replace(/_/g, " ")}
                      </TableCell>
                      {ACTIONS.map((action) => (
                        <TableCell key={action}>
                          <Switch
                            checked={Boolean(matrix[action])}
                            disabled={selected.key === "super_admin"}
                            onCheckedChange={(checked) => updateCell(module, action, checked)}
                            data-testid={`permission-${selected.key}-${module}-${action}`}
                          />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            <div className="mt-4 flex justify-end">
              <Button
                onClick={saveMatrix}
                disabled={saving || selected.key === "super_admin"}
                data-testid="save-hrms-permissions-button"
                className="bg-[#255058] text-white hover:bg-[#1c3d43]"
              >
                <Save className="mr-1.5 h-4 w-4" /> Save matrix
              </Button>
            </div>
          </>
        )}
      </Card>

      <Card className="p-5" data-testid="super-admin-override-card">
        <div className="font-display text-lg font-semibold">Super Admin override</div>
        <div className="mt-1 text-sm text-muted-foreground">
          Every edit, archive, and restore operation requires a reason and creates an immutable audit event.
        </div>
        <div className="mt-4 grid gap-3 lg:grid-cols-4">
          <div>
            <Label className="text-xs">Module</Label>
            <Select
              value={override.module}
              onValueChange={(module) => setOverride({ ...override, module })}
            >
              <SelectTrigger data-testid="override-module-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {[
                  "employee",
                  "attendance",
                  "leave",
                  "request",
                  "payroll",
                  "candidate",
                  "client",
                  "job",
                  "placement",
                  "timesheet",
                ].map((module) => (
                  <SelectItem key={module} value={module}>
                    {module.replace(/_/g, " ")}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Record ID</Label>
            <Input
              value={override.recordId}
              onChange={(event) => setOverride({ ...override, recordId: event.target.value })}
              data-testid="override-record-id-input"
            />
          </div>
          <div className="lg:col-span-2">
            <Label className="text-xs">Reason</Label>
            <Input
              value={override.reason}
              onChange={(event) => setOverride({ ...override, reason: event.target.value })}
              data-testid="override-reason-input"
            />
          </div>
        </div>
        <div className="mt-3">
          <Label className="text-xs">Fields to update (JSON)</Label>
          <Textarea
            value={override.changes}
            onChange={(event) => setOverride({ ...override, changes: event.target.value })}
            data-testid="override-changes-input"
          />
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          <Button
            onClick={() => runOverride("edit")}
            disabled={saving}
            data-testid="override-edit-button"
            className="bg-[#255058] text-white hover:bg-[#1c3d43]"
          >
            Save override
          </Button>
          <Button
            variant="outline"
            onClick={() => runOverride("archive")}
            disabled={saving}
            data-testid="override-archive-button"
          >
            Archive
          </Button>
          <Button
            variant="outline"
            onClick={() => runOverride("restore")}
            disabled={saving}
            data-testid="override-restore-button"
          >
            Restore
          </Button>
        </div>
      </Card>

      <Card className="overflow-hidden" data-testid="audit-log-tab">
        <div className="flex flex-wrap items-end justify-between gap-4 border-b border-border p-5">
          <div>
            <div className="font-display text-lg font-semibold">Audit Log</div>
            <div className="mt-1 text-sm text-muted-foreground">
              Append-only governance history for every recorded system change.
            </div>
          </div>
          <Button
            variant="outline"
            onClick={exportAudit}
            data-testid="audit-log-export-button"
          >
            <Download className="mr-1.5 h-4 w-4" /> Export CSV
          </Button>
        </div>
        <div className="grid gap-3 border-b border-border p-5 sm:grid-cols-2 lg:grid-cols-5">
          <div>
            <Label className="text-xs">User ID</Label>
            <Input
              value={filters.actor_id}
              onChange={(event) => setFilters({ ...filters, actor_id: event.target.value })}
              data-testid="audit-filter-user-input"
            />
          </div>
          <div>
            <Label className="text-xs">Module</Label>
            <Select
              value={filters.module}
              onValueChange={(module) => setFilters({ ...filters, module })}
            >
              <SelectTrigger data-testid="audit-filter-module-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All modules</SelectItem>
                {["employee", "attendance", "leave", "payroll", "hr_employee"].map(
                  (module) => (
                    <SelectItem key={module} value={module}>
                      {module.replace(/_/g, " ")}
                    </SelectItem>
                  ),
                )}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">From</Label>
            <Input
              type="date"
              value={filters.start_date}
              onChange={(event) => setFilters({ ...filters, start_date: event.target.value })}
              data-testid="audit-filter-start-input"
            />
          </div>
          <div>
            <Label className="text-xs">To</Label>
            <Input
              type="date"
              value={filters.end_date}
              onChange={(event) => setFilters({ ...filters, end_date: event.target.value })}
              data-testid="audit-filter-end-input"
            />
          </div>
          <div className="flex items-end">
            <Button
              onClick={loadLogs}
              data-testid="audit-filter-apply-button"
              className="w-full bg-[#255058] text-white hover:bg-[#1c3d43]"
            >
              Apply filters
            </Button>
          </div>
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>User</TableHead>
                <TableHead>Module / Record</TableHead>
                <TableHead>Field</TableHead>
                <TableHead>Old / New</TableHead>
                <TableHead>Timestamp / IP</TableHead>
                <TableHead>Reason</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {logs.map((item) => (
                <TableRow key={item.id} data-testid={`audit-log-row-${item.id}`}>
                  <TableCell>{item.user_name || "—"}</TableCell>
                  <TableCell className="text-xs">
                    {item.record_type} · {String(item.record_id).slice(0, 12)}
                  </TableCell>
                  <TableCell>{item.field || item.action}</TableCell>
                  <TableCell className="max-w-56 text-xs">
                    <div className="break-words">{valueText(item.previous_value)}</div>
                    <div className="mt-1 break-words text-[#255058]">
                      {valueText(item.new_value)}
                    </div>
                  </TableCell>
                  <TableCell className="text-xs">
                    <div>{item.created_at ? new Date(item.created_at).toLocaleString() : "—"}</div>
                    <div className="text-muted-foreground">{item.ip || "—"}</div>
                  </TableCell>
                  <TableCell className="max-w-48 break-words text-xs">{item.reason || "—"}</TableCell>
                </TableRow>
              ))}
              {!logs.length && (
                <TableRow>
                  <TableCell colSpan={6} className="py-8 text-center text-sm text-muted-foreground">
                    No audit events match these filters.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </Card>
    </div>
  );
}